import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import logo from "../assets/images/White_Version.png";
import { useLocation } from "react-router-dom";
import { getAuthCookie } from "../utils/authCookies";
import { AppIcon } from "components/AppIcon";
import { SharedIcons, type IconName } from "assets/icons";

interface ModulePermission {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  status: boolean;
  volume: number;
}

interface Module {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  description: string;
  status: boolean;
  volume: number;
}

interface License {
  id: string;
  companyName: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  statuss: string;
  licenseValidity: string | null;
  billingCycle: string | null;
  billingCyclePrice: number | null;
  paymentMethod: string;
  licenseType: string;
  modules: Module[]; // Changed to Module[]
}

const BASE = "/signage";

/** Base URL for Work Drive (new tab). In Create React App, only REACT_APP_* is exposed to the browser. */
function getWorkDriveExternalUrl(menuUrl: string): string {
  const fromMenu = (menuUrl || "").trim();
  if (fromMenu) return fromMenu;
  return (
    process.env.REACT_APP_WORK_DRIVE_URL ||
    process.env.WORK_DRIVE_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).trim();
}

/** Base URL for Studio (new tab). */
function getStudioExternalUrl(): string {
  return (process.env.REACT_APP_STUDIO_BASE_URL || "").trim();
}

type MenuIconRef = IconName | string;

function isSharedIconName(icon?: string): icon is IconName {
  return Boolean(icon && Object.prototype.hasOwnProperty.call(SharedIcons, icon));
}

function renderMenuIcon(icon: MenuIconRef, alt = "") {
  if (isSharedIconName(icon)) {
    return (
      <AppIcon
        name={icon}
        size={18}
        alt={alt}
        className="sidebar-menu-icon"
      />
    );
  }
  return <i className={icon}></i>;
}

// Menu structure: parent > child > sub-child (ordered as per spec)
type MenuChild = { name: string; path: string; module_code?: string; icon?: string; subchildren?: MenuChild[] };
type MenuItem =
  | { type: "link"; name: string; path: string; icon: MenuIconRef; module_code?: string }
  | { type: "external"; name: string; url: string; icon: MenuIconRef; module_code?: string }
  | { type: "group"; name: string; icon: MenuIconRef; module_code?: string; children: MenuChild[] }
  | { type: "nested"; name: string; icon: MenuIconRef; module_code?: string; sections: { label: string; children: MenuChild[] }[] };

const MENU_STRUCTURE: MenuItem[] = [
  { type: "link", name: "Dashboard", path: `${BASE}/dashboard`, icon: "sidebarDashboard" },
  {
    type: "group",
    name: "Reports",
    icon: "sidebarReports",
    module_code: "REPORTS",
    children: [
      { name: "Sales Order Summary", path: `${BASE}/salesOrderReport` },
      { name: "Installer Jobs", path: `${BASE}/installerJobReport` },
      { name: "Installations Billing & Summary", path: `${BASE}/installationSummaryReport` },
      { name: "Removal Billing & Summary", path: `${BASE}/removalSummaryReport` },
    ],
  },
  {
    type: "group",
    name: "Business",
    icon: "sidebarBusiness",
    children: [
      { name: "Brokerage Firms", path: `${BASE}/brokerageFirms`, module_code: "BROKERAGE_FIRMS" },
      { name: "Brokerage Teammembers", path: `${BASE}/brokerageTeammembers`, module_code: "BROKERAGE_TEAM_MEMBERS" },
      { name: "Agents", path: `${BASE}/agents`, module_code: "AGENTS" },
      { name: "Groups", path: `${BASE}/groups`, module_code: "GROUPS" },
    ],
  },
  {
    type: "group",
    name: "Orders & Fulfillment",
    icon: "sidebarOrders",
    module_code: "ORDERS",
    children: [{ name: "Orders", path: `${BASE}/orders` }],
  },
  {
    type: "group",
    name: "Jobs & Operations",
    icon: "sidebarJobs",
    module_code: "JOBS",
    children: [
      { name: "Installations", path: `${BASE}/jobs/installations` },
      { name: "Active Installations", path: `${BASE}/active-installations` },
      { name: "Removal Requests", path: `${BASE}/jobs/removalRequests` },
      { name: "Production Overview", path: `${BASE}/productionOverview` },
    ],
  },
  {
    type: "link",
    name: "Projects",
    path: `${BASE}/projects`,
    icon: "sidebarProjects",
    module_code: "PROJECTS",
  },
  {
    type: "group",
    name: "Studio",
    icon: "sidebarStudio",
    module_code: "STUDIO",
    children: [
      { name: "Studio", path: getStudioExternalUrl(), module_code: "STUDIO" },
      { name: "Designs", path: `${BASE}/designs`, module_code: "DESIGNS" },
    ],
  },
  {
    type: "group",
    name: "Products & Inventory",
    icon: "sidebarProducts",
    // No module_code: "INVENTORY" - API uses granular codes. Show if any child has permission.
    children: [
      {
        name: "Inventory",
        path: `${BASE}/products`,
        subchildren: [
          { name: "Standard Inventory", path: `${BASE}/products`, module_code: "STANDARD_INVENTORY" },
          { name: "Agent Inventory", path: `${BASE}/agentInventory`, module_code: "AGENT_INVENTORY" },
          { name: "Office Inventory", path: `${BASE}/officeInventory`, module_code: "OFFICE_INVENTORY" },
        ],
      },
      {
        name: "Catalog Setup",
        path: `${BASE}/categories`,
        subchildren: [
          { name: "Categories", path: `${BASE}/categories`, module_code: "CATEGORIES" },
          { name: "Brands", path: `${BASE}/brands`, module_code: "BRANDS" },
          { name: "Attributes", path: `${BASE}/attributes`, module_code: "ATTRIBUTES" },
        ],
      },
      { name: "Assets", path: `${BASE}/rotationalProducts`, module_code: "ROTATIONAL_PRODUCTS" },
      { name: "Warehouses", path: `${BASE}/warehouses`, module_code: "WAREHOUSES" },
    ],
  },
  {
    type: "group",
    name: "Incoming",
    icon: "sidebarIncoming",
    children: [
      { name: "Agent Products", path: `${BASE}/incomingAgentProducts`, module_code: "AGENT_INCOMING_PRODUCTS" },
      { name: "Office Products", path: `${BASE}/incomingOfficeProducts`, module_code: "OFFICE_INCOMING_PRODUCTS" },
    ],
  },
  {
    type: "group",
    name: "Finance",
    icon: "sidebarFinance",
    children: [
      { name: "Batch Payments", path: `${BASE}/batchPayments`, module_code: "BATCH_PAYMENTS" },
      { name: "Payment Methods", path: `${BASE}/paymentMethods`, module_code: "PAYMENT_METHODS" },
      { name: "Special Fees", path: `${BASE}/specialfees`, module_code: "SPECIAL_FEE" },
      { name: "Discounts", path: `${BASE}/SignageDiscounts`, module_code: "DISCOUNTS" },
    ],
  },
  {
    type: "group",
    name: "Shipments",
    icon: "sidebarShipments",
    // No parent module_code - show if any child has permission (backend uses SHIPPING_COST, SHIPPING_PRESETS, etc.)
    children: [
      { name: "Shipping Presets", path: `${BASE}/shippingpresets`, module_code: "SHIPPING_PRESETS" },
      { name: "Buy Label", path: `${BASE}/buylabels`, module_code: "BUY_LABELS" },
      { name: "Postage Wallet", path: `${BASE}/postagewallet`, module_code: "POSTAGE_WALLET" },
      { name: "Shipping Cost", path: `${BASE}/shipping/shipping-costs`, module_code: "SHIPPING_COST" },
    ],
  },
  { type: "link", name: "Post Types", path: `${BASE}/posttypes`, icon: "sidebarPostTypes", module_code: "POST_TYPE" },
  {
    type: "external",
    name: "Work Drive",
    url: "",
    icon: "sidebarWorkDrive",
    module_code: "WORK_DRIVE",
  },
  { type: "link", name: "Integrations", path: `${BASE}/integrations`, icon: "sidebarIntegrations", module_code: "INTEGRATIONS" },
  {
    type: "group",
    name: "Configuration",
    icon: "sidebarConfiguration",
    children: [
      {
        name: "My Organization",
        path: `${BASE}/addresses`,
        subchildren: [
          { name: "Roles & Permissions", path: `${BASE}/roles`, module_code: "ROLES" },
          { name: "Team Members", path: `${BASE}/teammembers`, module_code: "SIGNAGE_TEAM_MEMBERS" },
        ],
      },
      {
        name: "Settings",
        path: `${BASE}/settings`,
        module_code: "SETTINGS",
        subchildren: [
          { name: "General", path: `${BASE}/generalSettings`, module_code: "GENERAL_SETTINGS" },
          { name: "Invoice Templates", path: `${BASE}/InvoiceTemplates` },
          {
            name: "Email Templates",
            path: `${BASE}/SignageemailTeampleates`,
            module_code: "EMAIL_TEMPLATE",
          },
          { name: "Dynamic Costs", path: `${BASE}/dynamic-costs` },
          { name: "Migrations", path: `${BASE}/migrations` ,module_code:"USER_MIGRATION"},
          {
            name: "Tax Settings",
            path: `${BASE}/taxRates`,
            subchildren: [
              { name: "Geo Locations", path: `${BASE}/geoLocations` },
              { name: "Tax Rates", path: `${BASE}/taxRates` },
              { name: "Tax Rules", path: `${BASE}/taxRules` },
              { name: "Tax Groups", path: `${BASE}/taxGroups` },
            ],
          },
          { name: "Overdue Trigger Settings", path: `${BASE}/overdueSettings` },
        ],
      },
    ],
  },
  {
    type: "group",
    name: "Marketplace",
    icon: "sidebarMarketplace",
    children: [{ name: "Marketplace Profile", path: `${BASE}/marketplace_profile` }],
  },
];

const SignageSidebar = () => {
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [license, setLicense] = useState<License | null>(null);
  const [modulePermissions, setModulePermissions] = useState<Module[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // useEffect(() => {
  //   const path = location.pathname;

  //   if (path.startsWith("/signage/jobs")) {
  //     setOpenMenu("jobsMenu");
  //   }
  //   else if (path.startsWith("/signage/products")) {
  //     setOpenMenu("inventoryMenu");
  //   }
  //   else if (path.startsWith("/signage/orders")) {
  //     setOpenMenu("salesMenu");
  //   }
  //   else if (path.startsWith("/signage/roles")) {
  //     setOpenMenu("teamMenu");
  //   }
  // }, [location.pathname]);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch license data");
        }

        const data = await response.json();
        // console.log("Fetched License Data:", data);

        if (data && data.firmDetails && data.firmDetails.address) {
          const fetchedLicense: License = {
            id: data.id || "",
            companyName: data.firmDetails.companyName || "",
            jobTitle: data.jobTitle || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone1 || "",
            addressLine1: data.firmDetails.address.addressLine1 || "",
            addressLine2: data.firmDetails.address.addressLine2 || "",
            city: data.firmDetails.address.city || "",
            state: data.firmDetails.address.state || "",
            zip: data.firmDetails.address.zip || "",
            country: data.firmDetails.address.country || "",
            statuss: data.status || "Active",
            licenseValidity: data.licenseValidity || null,
            billingCycle: data.billingCycle || null,
            billingCyclePrice: data.billingCyclePrice || null,
            paymentMethod: data.paymentMethod || "",
            licenseType: data.licenseType || "",
            modules: Array.isArray(data.modulePermissions)
              ? data.modulePermissions
              : [],
          };

          setLicense(fetchedLicense);
          setModulePermissions(
            Array.isArray(data.modulePermissions) ? data.modulePermissions : []
          );
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching license data:", error);
        setError("Failed to fetch license data.");
      } finally {
        setLoading(false);
      }
    };

    if (!license) {
      fetchLicense();
    }
  }, [userId, license, token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.innerWidth < 992) {
      document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
    }
  }, [location.pathname]);

  return (
    <div className="app-menu navbar-menu">
      {/* ✅ MOBILE top strip (logo + close), visible on ≤767px */}
      <div className="mobile-sidebar-header d-md-none d-flex align-items-center gap-2 px-3 py-2">
        <Link to="/signage/welcome" className="d-inline-flex align-items-center text-decoration-none">
          <img src={logo} alt="Realtor" height={100} />
        </Link>
        <button
          type="button"
          aria-label="Close sidebar"
          className="btn btn-sm btn-icon btn-ghost-light ms-auto mt-3"
          onClick={() => {
            document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
          }}
        >
          <i className="ri-close-line fs-4"></i>
        </button>
      </div>
      <div className="navbar-brand-box d-none d-md-block">
        {/* Logo */}
        <Link to="/signage/welcome" className="logo logo-dark">
          <span className="logo-sm">
            <img src={logo} alt="" height="22" />
          </span>
          <span className="logo-lg">
            <img src={logo} alt="" height="40" />
          </span>
        </Link>

        <Link to="/signage/welcome" className="logo logo-light">
          <span className="logo-sm">
            <img src={logo} alt="" height="22" />
          </span>
          <span className="logo-lg">
            <img src={logo} alt="" height="90" style={{ marginTop: "10px" }} />
          </span>
        </Link>

        <button
          type="button"
          className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover"
          id="vertical-hover"
        >
          <i className="ri-record-circle-line"></i>
        </button>
      </div>

      {/* User Dropdown */}
      <div className="dropdown sidebar-user m-1 rounded">
        <button
          type="button"
          className="btn material-shadow-none"
          id="page-header-user-dropdown"
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          <span className="d-flex align-items-center gap-2">
            <img
              className="rounded header-profile-user"
              src="/assets/images/users/avatar-1.jpg"
              alt="Header Avatar"
            />
            <span className="text-start">
              <span className="d-block fw-medium sidebar-user-name-text">
                .......
              </span>
              <span className="d-block fs-14 sidebar-user-name-sub-text">
                <i className="ri-circle-fill fs-10 text-success align-baseline"></i>
                <span className="align-middle">Online</span>
              </span>
            </span>
          </span>
        </button>
        <div className="dropdown-menu dropdown-menu-end">
          <h6 className="dropdown-header">Welcome Anna!</h6>
          <Link to="/profile" className="dropdown-item">
            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Profile</span>
          </Link>
          <Link to="/messages" className="dropdown-item">
            <i className="mdi mdi-message-text-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Messages</span>
          </Link>
          <Link to="/tasks" className="dropdown-item">
            <i className="mdi mdi-calendar-check-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Taskboard</span>
          </Link>
          <Link to="/help" className="dropdown-item">
            <i className="mdi mdi-lifebuoy text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Help</span>
          </Link>
          <div className="dropdown-divider"></div>
          <Link to="/profile" className="dropdown-item">
            <i className="mdi mdi-wallet text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">
              Balance: <b>$5971.67</b>
            </span>
          </Link>
          <Link to="/settings" className="dropdown-item">
            <i className="mdi mdi-cog-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Settings</span>
          </Link>
          <Link to="/lockscreen" className="dropdown-item">
            <i className="mdi mdi-lock text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Lock screen</span>
          </Link>
          <Link to="/logout" className="dropdown-item">
            <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Logout</span>
          </Link>
        </div>
      </div>

      {/* Sidebar Navigation */}
      <div id="scrollbar">
        <div className="container-fluid">
          <ul className="navbar-nav" id="navbar-nav">
            {/* <li className="nav-item">
              <Link to="/signage/welcome" className={`nav-link menu-link ${location.pathname === "/signage/welcome" ? "active" : ""}`}>
                <Star size={28} color="#ffffff" className="ms-1" />
                <span data-key="t-dashboards">Welcome</span>
              </Link>
            </li> */}
            {MENU_STRUCTURE.map((item, idx) => {
              const menuId = `menu-${idx}`;
              const hasPermission = (code?: string) => !code || modulePermissions?.find((p) => p.module_code === code)?.status;
              const hasAnyChildPermission = (children: MenuChild[]) =>
                children.some((c) => hasPermission(c.module_code));

              if (item.type === "link") {
                if (!hasPermission(item.module_code)) return null;
                return (
                  <li className="nav-item" key={menuId}>
                    <Link to={item.path} className={`nav-link menu-link ${location.pathname === item.path ? "active" : ""}`}>
                      {renderMenuIcon(item.icon, item.name)}
                      <span>{item.name}</span>
                    </Link>
                  </li>
                );
              }

              if (item.type === "external") {
                if (!hasPermission(item.module_code)) return null;
                const href = getWorkDriveExternalUrl(item.url);
                if (!href) return null;
                return (
                  <li className="nav-item" key={menuId}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link menu-link"
                    >
                      {renderMenuIcon(item.icon, item.name)}
                      <span>{item.name}</span>
                    </a>
                  </li>
                );
              }

              if (item.type === "group") {
                const show = item.module_code ? hasPermission(item.module_code) : hasAnyChildPermission(item.children);
                if (!show) return null;
                const childrenToShow = item.children.filter((c) => hasPermission(c.module_code));
                if (childrenToShow.length === 0) return null;

                // Parent stays open when this menu OR any nested child is open
                const isParentOpen = (id: string) => openMenu === id || (openMenu?.startsWith(id + "-") ?? false);

                return (
                  <li className="nav-item" key={menuId}>
                    <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === menuId ? null : menuId)} href={`#${menuId}`} data-bs-toggle="collapse" role="button" aria-expanded="false">
                      {renderMenuIcon(item.icon, item.name)}
                      <span>{item.name}</span>
                    </a>
                    <div className={`collapse menu-dropdown ${isParentOpen(menuId) ? "show" : ""}`} id={menuId}>
                      <ul className="nav nav-sm flex-column">
                        {childrenToShow.map((child) => {
                          const renderChild = (c: MenuChild, parentId: string): React.ReactNode => {
                            const subId = `${parentId}-${c.name.replace(/\s/g, "-")}`;
                            if (c.subchildren) {
                              const visibleSubs = c.subchildren.filter((s) => hasPermission(s.module_code));
                              if (visibleSubs.length === 0) return null;
                              return (
                                <li className="nav-item" key={c.path}>
                                  <a className="nav-link" href={`#${subId}`} data-bs-toggle="collapse" role="button" onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === subId ? parentId : subId); }}>
                                    {c.name}
                                  </a>
                                  <div className={`collapse menu-dropdown ${isParentOpen(subId) ? "show" : ""}`} id={subId}>
                                    <ul className="nav nav-sm flex-column">
                                      {visibleSubs.map((sub) => renderChild(sub, subId))}
                                    </ul>
                                  </div>
                                </li>
                              );
                            }
                            return (
                              <li className="nav-item" key={c.path}>
                                <Link to={c.path} className={`nav-link ${location.pathname === c.path ? "active" : ""}`}>{c.name}</Link>
                              </li>
                            );
                          };
                          return child.subchildren ? (
                            <React.Fragment key={child.path}>
                              {renderChild(child, menuId)}
                            </React.Fragment>
                          ) : (
                            <li className="nav-item" key={child.path}>
                              {item.name === "Studio" && child.name === "Studio" ? (
                                <a
                                  href={child.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="nav-link"
                                >
                                  {child.name}
                                </a>
                              ) : (
                                <Link to={child.path} className={`nav-link ${location.pathname === child.path ? "active" : ""}`}>{child.name}</Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                );
              }

              if (item.type === "nested") {
                if (!hasPermission(item.module_code)) return null;
                return (
                  <li className="nav-item" key={menuId}>
                    <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === menuId ? null : menuId)} href={`#${menuId}`} data-bs-toggle="collapse" role="button" aria-expanded="false">
                      {renderMenuIcon(item.icon, item.name)}
                      <span>{item.name}</span>
                    </a>
                    <div className={`collapse menu-dropdown ${openMenu === menuId ? "show" : ""}`} id={menuId}>
                      {item.sections.map((sec, secIdx) => (
                        <div key={secIdx}>
                          <div className="nav-link small text-muted px-3 py-1">{sec.label}</div>
                          <ul className="nav nav-sm flex-column">
                            {sec.children.map((c) => (
                              <li className="nav-item" key={c.path}>
                                <Link to={c.path} className={`nav-link ${location.pathname === c.path ? "active" : ""}`}>{c.name}</Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </li>
                );
              }
              return null;
            })}

            <li className="nav-item">
              <Link to="/signage/profile" className={`nav-link menu-link ${location.pathname === "/signage/profile" ? "active" : ""}`}>
                {renderMenuIcon("sidebarProfile", "Profile")}
                <span data-key="t-dashboards">Profile</span>
              </Link>
            </li>
            <li className="nav-item">
              <LogoutButton
                icon={<AppIcon name="sidebarLogout" size={18} alt="Logout" className="sidebar-menu-icon" />}
              />
            </li>
            <style>{`
              .navbar-menu .navbar-nav .menu-link .sidebar-menu-icon {
                width: 18px;
                height: 18px;
                margin-right: 0.35rem;
                vertical-align: middle;
                flex-shrink: 0;
              }
            `}</style>
            {/* <li className="nav-item">
              <Link to="/signage/chat" className={`nav-link menu-link ${location.pathname === "/signage/chat" ? "active" : ""}`}>
                <i className="mdi mdi-message-text-outline"></i>
                <span data-key="t-dashboards">Chat </span>
              </Link>
            </li>

            <li className="nav-item">
              <Link to="/signage/calendar" className={`nav-link menu-link ${location.pathname === "/signage/calendar" ? "active" : ""}`}>
                <i className="ri-calendar-event-fill"></i>
                <span data-key="t-dashboards">Calendar </span>
              </Link>
            </li>

            <li className="nav-item">
              <Link to="/signage/todo-list" className={`nav-link menu-link ${location.pathname === "/signage/todo-list" ? "active" : ""}`}>
                <i className="ri-todo-fill"></i>
                <span data-key="t-dashboards">To do list </span>
              </Link>
            </li>

            <li className="nav-item">
              <Link to="/signage/file-manager" className={`nav-link menu-link ${location.pathname === "/signage/file-manager" ? "active" : ""}`}>
                <i className="ri-file-copy-fill"></i>
                <span data-key="t-dashboards">File manager </span>
              </Link>
            </li> */}
          </ul>
        </div>
      </div>

      <div
        className="sidebar-background"
        onClick={() =>
          document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable")
        }
      ></div>
    </div>
  );
};

export default SignageSidebar;
